
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    MdAdd,
    MdVisibility,
    MdEdit,
    MdDelete,
    MdMoreVert,
    MdDownload,
    MdNote,
    MdShare,
    MdClose,
    MdSearch
} from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import Select from 'react-select'; // Import Select for vendor dropdown
import { useUser } from '../context/UserContext';
import './GenerateRequests.css';
import '../pages/Dashboard.css';
import Swal from 'sweetalert2';
import axios from 'axios';
import {
    DELETE_PURCHASE_ORDER_API,
    SEND_WHATSAPP_LINK_API,
    GET_WHATSAPP_CREDS_API,
    VIEW_PURCHASE_ORDER_API,
    GET_COMPLETED_REQUESTS_API,
    GET_PENDING_REQUESTS_API,
    GET_REMAINING_VENDOR_LIST_API,
    ADD_VENDOR_TO_REQUEST_API
} from '../config/API';
import { encryptToken } from '../utils/secureToken';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import ActionMenuPortal from '../components/common/ActionMenuPortal';
import Modal from '../components/common/Modal'; // Import Modal component
import { formatDate } from '../utils/dateUtils';

const GenerateRequests = () => {
    const navigate = useNavigate();
    const [purchaseOrders, setGenerateRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [activeTab, setActiveTab] = useState('pending');
    const [pendingSearch, setPendingSearch] = useState('');
    const [completedSearch, setCompletedSearch] = useState('');

    // WhatsApp Share State
    const [showWhatsappModal, setShowWhatsappModal] = useState(false);
    const [selectedVendorsForWhatsapp, setSelectedVendorsForWhatsapp] = useState([]);
    const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
    const [whatsappCreds, setWhatsappCreds] = useState(null);
    const [selectedPoForWhatsapp, setSelectedPoForWhatsapp] = useState(null);
    const [loadingVendors, setLoadingVendors] = useState(false);
    const [whatsappMessage, setWhatsappMessage] = useState("Please click on this link to update rate for the generate request.");
    const [whatsappLoading, setWhatsappLoading] = useState(false);
    const [whatsappProgress, setWhatsappProgress] = useState({ current: 0, total: 0, vendorName: '' });
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const { hasPermission } = useUser();

    // Add Vendor State
    const [showAddVendorModal, setShowAddVendorModal] = useState(false);
    const [selectedPoForAddVendor, setSelectedPoForAddVendor] = useState(null);
    const [remainingVendors, setRemainingVendors] = useState([]);
    const [selectedVendorForAdd, setSelectedVendorForAdd] = useState(null);
    const [loadingRemainingVendors, setLoadingRemainingVendors] = useState(false);
    const [addingVendor, setAddingVendor] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const filteredGenerateRequests = purchaseOrders.filter(po => {
        const status = (po.status || 'pending').toLowerCase().trim();
        const isCompleted = ['completed', 'delivered'].includes(status);

        if (activeTab === 'completed') {
            return isCompleted;
        } else {
            // Pending tab: skip only explicitly completed/delivered items
            return !isCompleted;
        }
    });

    const fetchGenerateRequests = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const api = activeTab === 'completed' ? GET_COMPLETED_REQUESTS_API : GET_PENDING_REQUESTS_API;
            const searchTerm = activeTab === 'completed' ? completedSearch : pendingSearch;

            const response = await axios.get(api, {
                params: {
                    page: currentPage,
                    limit: 10,
                    search: searchTerm
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                setGenerateRequests(response.data.data || []);
                const pagination = response.data.pagination || {};
                setTotalPages(pagination.totalPages || 1);
                setTotalRecords(pagination.total || 0);
            }
        } catch (error) {
            console.error('Error fetching purchase orders:', error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, activeTab, pendingSearch, completedSearch]);

    useEffect(() => {
        fetchGenerateRequests();
    }, [fetchGenerateRequests]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);


    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
            setAnchorRect(null);
        } else {
            setOpenMenuId(id);
            setAnchorRect(e.currentTarget.getBoundingClientRect());
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.swal2-container')) return;
            if (event.target.closest('.action-portal-wrapper')) return;
            if (event.target.closest('.action-menu-btn')) return;

            setOpenMenuId(null);
            setAnchorRect(null);
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Generate Request?',
            text: 'Are you sure you want to delete this Request?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-delete-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('accessToken');
                const response = await axios.delete(DELETE_PURCHASE_ORDER_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire('Deleted!', 'Purchase order has been deleted.', 'success');

                    // Optimistic update: remove the deleted item from current state
                    setGenerateRequests(prev => prev.filter(po => po.id !== id));

                    // If we deleted the last record on a page (other than page 1), go back one page
                    if (purchaseOrders.length === 1 && currentPage > 1) {
                        setCurrentPage(prev => prev - 1);
                    } else {
                        // Otherwise, manually refresh the list to fill the gap from the next page
                        fetchGenerateRequests();
                    }
                } else {
                    Swal.fire('Error', response.data.error || response.data.message || 'Failed to delete Request', 'error');
                }
            } catch (error) {
                console.error('Error deleting Request:', error);
                Swal.fire('Error', error.response?.data?.error || error.response?.data?.message || 'An error occurred while deleting', 'error');
            }
            setOpenMenuId(null);
        }
    }


    const handleSendWhatsapp = async () => {
        const vendorsToSend = selectedVendorsForWhatsapp.filter(v => v.isChecked);
        if (vendorsToSend.length === 0) {
            Swal.fire('Error', 'Please select at least one vendor.', 'error');
            return;
        }

        const token = localStorage.getItem('accessToken');
        let currentCreds = whatsappCreds;

        // if (!currentCreds?.authKey || !currentCreds?.appKey) {
        //     try {
        //         const response = await axios.get(GET_WHATSAPP_CREDS_API, {
        //             headers: { Authorization: `Bearer ${token}` }
        //         });
        //         if (response.data.status && response.data.data) {
        //             currentCreds = {
        //                 authKey: response.data.data.authKey || '',
        //                 appKey: response.data.data.appKey || ''
        //             };
        //             setWhatsappCreds(currentCreds);
        //         }
        //     } catch (error) {
        //         console.error("Error fetching fallback credentials:", error);
        //     }
        // }

        // if (!currentCreds?.authKey || !currentCreds?.appKey) {
        //     Swal.fire('Error', 'WhatsApp credentials not found. Please configure them in settings.', 'error');
        //     return;
        // }

        setSendingWhatsapp(true);
        setWhatsappLoading(true);
        setWhatsappProgress({ current: 0, total: vendorsToSend.length, vendorName: '' });
        let successCount = 0;
        let failCount = 0;

        const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

        for (let i = 0; i < vendorsToSend.length; i++) {
            const vendor = vendorsToSend[i];

            setWhatsappProgress({
                current: i + 1,
                total: vendorsToSend.length,
                vendorName: vendor.vendorName
            });

            if (!vendor.mobileNumber) { failCount++; continue; }

            // --- Control Delay between consecutive calls ---
            if (i > 0) await sleep(2000); // 2 second delay between calls

            try {
                const encryptedRequestId = encryptToken(Number(selectedPoForWhatsapp.id));
                const encryptedVendorId = encryptToken(Number(vendor.vendorId));
                const portalLink = `${window.location.origin}/vendor-portal/${encryptedRequestId}/${encryptedVendorId}`;

                // ✅ Include vendorId so backend targets only this vendor
                //    and skips admin notifications for this call
                const payload = {
                    vendorId: vendor.vendorId,
                    message: `${whatsappMessage}\n\n${portalLink}`,
                    link: portalLink
                };

                const response = await axios.post(SEND_WHATSAPP_LINK_API(selectedPoForWhatsapp.id), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // ✅ Check for internal failures in response data even if status is 200/true
                const resDataArr = response.data?.data;
                const internalError = Array.isArray(resDataArr) && resDataArr.length > 0 && resDataArr[0].error;

                if (internalError) {
                    console.error(`Internal WhatsApp failure for ${vendor.vendorName}:`, internalError);
                    failCount++;
                } else {
                    successCount++;
                }
            } catch (error) {
                console.error(`Failed to send WhatsApp to ${vendor.vendorName}:`, error);
                failCount++;
            }
        }

        // ✅ TRIGGER ADMIN NOTIFICATIONS: Exactly once after all vendors are processed.
        // We pass NO 'vendorId' so the backend knows this is an admin alert.
        if (successCount > 0) {
            try {
                await axios.post(
                    SEND_WHATSAPP_LINK_API(selectedPoForWhatsapp.id),
                    { message: "ignore_this_message" }, // Backend uses its own hardcoded "Link created" message
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } catch (error) {
                console.error('Failed to send admin notification:', error);
            }
        }

        setWhatsappLoading(false);
        setSendingWhatsapp(false);
        setShowWhatsappModal(false);

        if (successCount > 0 && failCount === 0) {
            Swal.fire('Success', `WhatsApp message sent to ${successCount} vendor(s).`, 'success');
        } else if (successCount > 0 && failCount > 0) {
            Swal.fire('Partial Success', `Sent to ${successCount} vendor(s), failed for ${failCount}.`, 'warning');
        } else {
            Swal.fire('Error', 'Failed to send WhatsApp messages.', 'error');
        }
    };


    const openWhatsappModal = async (po) => {
        setOpenMenuId(null);
        setSelectedPoForWhatsapp(po);
        setShowWhatsappModal(true);
        setLoadingVendors(true);

        try {
            const token = localStorage.getItem('accessToken');

            // Fetch full PO details to ensure we have all vendor info (especially phone numbers)
            const poResponse = await axios.get(VIEW_PURCHASE_ORDER_API(po.id), {
                headers: { Authorization: `Bearer ${token}` }
            });

            const requestVendors = poResponse.data.data.RequestVendors || poResponse.data.data.PurchaseOrderVendors || [];
            const uniqueVendors = requestVendors.length
                ? [...new Map(requestVendors.map(v => [v.Vendor?.id || v.Vendor?._id, v])).values()]
                : [];

            const formattedVendors = uniqueVendors.map(v => {
                const vendorData = v.Vendor || {};
                // Check multiple possible key variations for the phone number
                const mobileNumber = vendorData.whatsappNumber ||
                    vendorData.whatsAppNumber ||
                    vendorData.whatsapp_number ||
                    vendorData.contactNumber ||
                    vendorData.mobileNumber || '';

                return {
                    vendorId: vendorData.id || v.vendorId,
                    vendorName: vendorData.vendorName || 'Unknown Vendor',
                    mobileNumber: mobileNumber,
                    // countryCode: vendorData.countryCode || '91',
                    isChecked: true
                };
            });

            setSelectedVendorsForWhatsapp(formattedVendors);

            // Fetch WhatsApp Creds
            // const userStr = localStorage.getItem('user');
            // if (userStr) {
            //     const user = JSON.parse(userStr);
            //     const response = await axios.get(GET_WHATSAPP_CREDS_API, {
            //         headers: { Authorization: `Bearer ${token}` }
            //     });
            //     if (response.data.status && response.data.data) {
            //         setWhatsappCreds({
            //             authKey: response.data.data.authKey || '',
            //             appKey: response.data.data.appKey || ''
            //         });
            //     }
            // }
        } catch (error) {
            console.error('Error fetching details for WhatsApp modal:', error);
            Swal.fire('Error', 'Failed to load vendor details.', 'error');
        } finally {
            setLoadingVendors(false);
        }
    };

    const handleWhatsappCheckboxChange = (vendorId) => {
        setSelectedVendorsForWhatsapp(prev => prev.map(v =>
            v.vendorId === vendorId ? { ...v, isChecked: !v.isChecked } : v
        ));
    };

    const openAddVendorModal = async (po) => {
        setOpenMenuId(null);
        setSelectedPoForAddVendor(po);
        setSelectedVendorForAdd(null);
        setShowAddVendorModal(true);
        setLoadingRemainingVendors(true);

        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_REMAINING_VENDOR_LIST_API(po.id), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                const vendors = response.data.remainingVendorList || [];
                const formattedOptions = vendors.map(v => ({
                    value: v.id,
                    label: v.companyName ? `${v.vendorName} (${v.companyName})` : v.vendorName,
                    vendorName: v.vendorName,
                    companyName: v.companyName
                }));
                setRemainingVendors(formattedOptions);
            } else {
                setRemainingVendors([]);
            }
        } catch (error) {
            console.error('Error fetching remaining vendors:', error);
            Swal.fire('Error', 'Failed to load available vendors.', 'error');
        } finally {
            setLoadingRemainingVendors(false);
        }
    };

    const handleAddVendorSubmit = async () => {
        if (!selectedVendorForAdd) {
            Swal.fire('Error', 'Please select a vendor.', 'error');
            return;
        }

        setAddingVendor(true);
        try {
            const token = localStorage.getItem('accessToken');
            const reqId = selectedPoForAddVendor.id;
            const vendorId = selectedVendorForAdd.value;

            // Generate Portal Link
            const encryptedRequestId = encryptToken(Number(reqId));
            const encryptedVendorId = encryptToken(Number(vendorId));
            const portalLink = `${window.location.origin}/vendor-portal/${encryptedRequestId}/${encryptedVendorId}`;

            const payload = {
                reqId: Number(reqId),
                vendorId: Number(vendorId),
                link: portalLink
            };

            const response = await axios.post(ADD_VENDOR_TO_REQUEST_API, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                Swal.fire('Success', 'Vendor added to request successfully.', 'success');
                setShowAddVendorModal(false);
                fetchGenerateRequests(); // Refresh the list
            } else {
                Swal.fire('Error', response.data.message || 'Failed to add vendor.', 'error');
            }
        } catch (error) {
            console.error('Error adding vendor:', error);
            Swal.fire('Error', error.response?.data?.message || 'An error occurred while adding vendor.', 'error');
        } finally {
            setAddingVendor(false);
        }
    };



    return (
        <div className="dashboard">
            <div className="purchase-orders-header">
                <h2 className="section-title" style={{ margin: 0 }}>Generate Requests</h2>
                {hasPermission('PURCHASE_ORDERS', 'create') && (
                    <Link to="/generate-requests/new" className="add-po-btn">
                        <MdAdd size={20} />
                        Add Generate Request
                    </Link>
                )}
            </div>

            <div className={isMobile ? "" : "card-container-po"}>

                {/* Tab-wise Search Bar */}
                <div className="search-container">
                    <div className="search-wrapper">
                        <MdSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder={`Search ${activeTab} requests by number, project or company...`}
                            className="search-input"
                            value={activeTab === 'pending' ? pendingSearch : completedSearch}
                            onChange={(e) => {
                                if (activeTab === 'pending') {
                                    setPendingSearch(e.target.value);
                                } else {
                                    setCompletedSearch(e.target.value);
                                }
                                setCurrentPage(1);
                            }}
                        />
                        {(activeTab === 'pending' ? pendingSearch : completedSearch) && (
                            <button
                                className="clear-search-btn"
                                onClick={() => {
                                    if (activeTab === 'pending') {
                                        setPendingSearch('');
                                    } else {
                                        setCompletedSearch('');
                                    }
                                    setCurrentPage(1);
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                <div className={isMobile ? "tabs-card" : ""} style={{
                    padding: '1rem',
                    borderBottom: isMobile ? 'none' : '1px solid #e5e7eb',
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap'
                }}>
                    <div style={{ display: 'flex', gap: '1rem', width: isMobile ? '100%' : 'auto' }}>
                        <button
                            className={`tab-btn ${activeTab === 'pending' ? 'active-tab' : ''}`}
                            onClick={() => {
                                setActiveTab('pending');
                                setCurrentPage(1);
                            }}
                            style={{
                                padding: '0.5rem 1rem',
                                border: 'none',
                                background: activeTab === 'pending' ? '#eef2ff' : 'transparent',
                                color: activeTab === 'pending' ? '#4f46e5' : '#6b7280',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                flex: isMobile ? 1 : 'none'
                            }}
                        >
                            Pending Requests
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'completed' ? 'active-tab' : ''}`}
                            onClick={() => {
                                setActiveTab('completed');
                                setCurrentPage(1);
                            }}
                            style={{
                                padding: '0.5rem 1rem',
                                border: 'none',
                                background: activeTab === 'completed' ? '#ecfdf5' : 'transparent',
                                color: activeTab === 'completed' ? '#059669' : '#6b7280',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                flex: isMobile ? 1 : 'none'
                            }}
                        >
                            Completed Requests
                        </button>
                    </div>
                </div>



                <div className="table-container">
                    <table>

                        <thead>
                            <tr>
                                <th>Request Number</th>
                                <th>Date</th>
                                <th>Project</th>
                                <th>Company Name</th>
                                <th>Total Amount</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6"><Loader text="Fetching purchase orders..." /></td></tr>
                            ) : filteredGenerateRequests.length === 0 ? (
                                <tr><td colSpan="7"><NoData text={`No ${activeTab} requests found.`} /></td></tr>
                            ) : (
                                filteredGenerateRequests.map((po, index) => (
                                    <tr key={po.id}>
                                        <td>
                                            <span className="po-number" style={{ fontFamily: 'monospace' }}>#{po.reqNumber.toUpperCase() || 'N/A'}</span>
                                        </td>
                                        <td>{formatDate(po.date)}</td>
                                        <td>
                                            <div className="project-info-text">
                                                {po.ProjectMaster?.projectName || po.Project?.projectName || po.projectName || 'N/A'}
                                            </div>
                                        </td>
                                        <td className="company-name-cell">
                                            {(() => {
                                                const companies = po.companyName
                                                    ? po.companyName.split(',').map(n => n.trim()).filter(Boolean)
                                                    : (Array.isArray(po.RequestVendors)
                                                        ? po.RequestVendors.map(v => v.Vendor?.companyName || v.Vendor?.vendorName).filter(Boolean)
                                                        : (po.RequestVendors?.Vendor?.companyName ? [po.RequestVendors.Vendor.companyName] : []));

                                                return companies.length > 0 ? (
                                                    <div className="items-stack">
                                                        {companies.map((name, idx) => (
                                                            <div key={idx} className="item-name">{name}</div>
                                                        ))}
                                                    </div>
                                                ) : 'N/A';
                                            })()}
                                        </td>

                                        <td>₹{po.grandTotal ? parseFloat(po.grandTotal).toLocaleString() : '0'}</td>
                                        <td>
                                            <span className={`status-badge status-${(po.status || 'pending').toLowerCase()}`}>
                                                {po.status || 'Pending'}
                                            </span>
                                        </td>
                                        <td className={`action-cell ${openMenuId === po.id ? 'menu-open' : ''}`}>
                                            <button
                                                className="action-menu-btn"
                                                onClick={(e) => toggleMenu(po.id, e)}
                                                title="Actions"
                                            >
                                                <MdMoreVert size={20} />
                                            </button>

                                            <ActionMenuPortal
                                                isOpen={openMenuId === po.id}
                                                anchorRect={anchorRect}
                                                onClose={() => setOpenMenuId(null)}
                                            >
                                                <div className="action-dropdown">
                                                    {hasPermission('PURCHASE_ORDERS', 'read') && (
                                                        <button className="action-item" onClick={() => navigate(`/generate-requests/view/${po.id}`)}>
                                                            <MdVisibility size={16} /> View Request
                                                        </button>
                                                    )}
                                                    {hasPermission('PURCHASE_ORDERS', 'update') && (
                                                        <button className="action-item" onClick={() => navigate(`/generate-requests/edit/${po.id}`)}>
                                                            <MdEdit size={16} /> Edit Request
                                                        </button>
                                                    )}
                                                    {/* <button className="action-item" onClick={() => navigate(`/generate-requests/${po.id}/notes`)}>
                                                        <MdNote size={16} /> Internal Notes
                                                    </button> */}
                                                    {/* <button className="action-item" onClick={() => Swal.fire('PDF', 'PDF generation functionality is not implemented as per request.', 'info')}>
                                                        <MdDownload size={16} /> Generate PDF
                                                    </button> */}
                                                    <button className="action-item" onClick={() => openWhatsappModal(po)}>
                                                        <FaWhatsapp size={16} /> Share on WhatsApp
                                                    </button>
                                                    {activeTab === 'pending' && hasPermission('PURCHASE_ORDERS', 'update') && (
                                                        <button className="action-item" onClick={() => openAddVendorModal(po)}>
                                                            <MdAdd size={16} /> Add Vendor
                                                        </button>
                                                    )}
                                                    {hasPermission('PURCHASE_ORDERS', 'delete') && (
                                                        <button
                                                            className="action-item delete"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDelete(po.id);
                                                            }}
                                                        >
                                                            <MdDelete size={16} /> Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </ActionMenuPortal>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View */}
                <div className="mobile-card-view">

                    {loading ? (
                        <Loader text="Fetching purchase orders..." />
                    ) : filteredGenerateRequests.length === 0 ? (
                        <NoData text={`No ${activeTab} requests found.`} />
                    ) : (
                        filteredGenerateRequests.map((po) => (
                            <div key={po.id} className="project-card">
                                <div className="card-header">
                                    <div className="card-title">
                                        <strong className="po-number">{po.poNumber}</strong>
                                        <span className={`status-badge status-${(po.status || 'pending').toLowerCase()}`}>
                                            {po.status || 'Pending'}
                                        </span>
                                    </div>
                                    <button
                                        className="action-menu-btn"
                                        onClick={(e) => toggleMenu(po.id, e)}
                                        title="Actions"
                                    >
                                        <MdMoreVert size={20} />
                                    </button>
                                    <ActionMenuPortal
                                        isOpen={openMenuId === po.id}
                                        anchorRect={anchorRect}
                                        onClose={() => setOpenMenuId(null)}
                                    >
                                        <div className="action-dropdown shadow-lg">
                                            {hasPermission('PURCHASE_ORDERS', 'read') && (
                                                <button className="action-item" onClick={() => navigate(`/generate-requests/view/${po.id}`)}>
                                                    <MdVisibility size={16} /> View Request
                                                </button>
                                            )}
                                            {hasPermission('PURCHASE_ORDERS', 'update') && (
                                                <button className="action-item" onClick={() => navigate(`/generate-requests/edit/${po.id}`)}>
                                                    <MdEdit size={16} /> Edit Request
                                                </button>
                                            )}
                                            {/* <button className="action-item" onClick={() => navigate(`/generate-requests/${po.id}/notes`)}>
                                                <MdNote size={16} /> Internal Notes
                                            </button>
                                            <button className="action-item" onClick={() => Swal.fire('PDF', 'PDF generation functionality is not implemented as per request.', 'info')}>
                                                <MdDownload size={16} /> Generate PDF
                                            </button> */}
                                            <button className="action-item" onClick={() => openWhatsappModal(po)}>
                                                <FaWhatsapp size={16} /> Share on WhatsApp
                                            </button>
                                            {activeTab === 'pending' && hasPermission('PURCHASE_ORDERS', 'update') && (
                                                <button className="action-item" onClick={() => openAddVendorModal(po)}>
                                                    <MdAdd size={16} /> Add Vendor
                                                </button>
                                            )}

                                            {hasPermission('PURCHASE_ORDERS', 'delete') && (
                                                <button
                                                    className="action-item delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(po.id);
                                                    }}
                                                >
                                                    <MdDelete size={16} /> Delete
                                                </button>
                                            )}
                                        </div>
                                    </ActionMenuPortal>
                                </div>
                                <div className="card-body">
                                    <div className="card-row align-top">
                                        <span className="card-label">Company Name:</span>
                                        <div className="card-value">
                                            {(() => {
                                                const companies = po.companyName
                                                    ? po.companyName.split(',').map(n => n.trim()).filter(Boolean)
                                                    : (Array.isArray(po.RequestVendors)
                                                        ? po.RequestVendors.map(v => v.Vendor?.companyName || v.Vendor?.vendorName).filter(Boolean)
                                                        : (po.RequestVendors?.Vendor?.companyName ? [po.RequestVendors.Vendor.companyName] : []));

                                                return companies.length > 0 ? (
                                                    <div className="items-stack">
                                                        {companies.map((name, idx) => (
                                                            <div key={idx} className="item-name">{name}</div>
                                                        ))}
                                                    </div>
                                                ) : 'N/A';
                                            })()}
                                        </div>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">Project:</span>
                                        <span className="card-value">{po.ProjectMaster?.projectName || po.Project?.projectName || po.projectName}</span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">Date:</span>
                                        <span className="card-value">{formatDate(po.date)}</span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">Total Amount:</span>
                                        <span className="card-value">₹{po.grandTotal ? parseFloat(po.grandTotal).toLocaleString() : '0'}</span>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
                <div className="pagination-card">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        totalRecords={totalRecords}
                        currentCount={filteredGenerateRequests.length}
                        entityName="Generate Requests"
                    />
                </div>
            </div>

            {/* WhatsApp Share Modal */}
            {
                showWhatsappModal && (
                    <div className="modal-overlay" onClick={() => setShowWhatsappModal(false)}>
                        <div className="modal-content share-modal-content" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Share on Whatsapp</h3>
                                <button className="close-btn" onClick={() => setShowWhatsappModal(false)}>
                                    <MdClose size={20} />
                                </button>
                            </div>
                            <div className="modal-body">
                                <p className="share-instruction">
                                    Select vendors to send the link via Whatsapp.
                                </p>

                                {/* <div className="whatsapp-message-section" style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem', color: '#374151' }}>
                                        Custom Message
                                    </label>
                                    <textarea
                                        className="whatsapp-message-input"
                                        value={whatsappMessage}
                                        onChange={(e) => setWhatsappMessage(e.target.value)}
                                        placeholder="Enter message here..."
                                        rows="3"
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: '1px solid #d1d5db',
                                            fontSize: '0.95rem',
                                            fontFamily: 'inherit',
                                            resize: 'vertical',
                                            outline: 'none',
                                            transition: 'border-color 0.2s'
                                        }}
                                    />
                                </div> */}

                                {loadingVendors ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                                        <Loader text="Loading vendor details..." />
                                    </div>
                                ) : selectedVendorsForWhatsapp.length === 0 ? (
                                    <p className="no-data">No vendors found for this order.</p>
                                ) : (
                                    <div className="vendor-links-list">
                                        {selectedVendorsForWhatsapp.map((vendor) => (
                                            <div key={vendor.vendorId} className="vendor-link-item">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <input
                                                        type="checkbox"
                                                        id={`vendor-${vendor.vendorId}`}
                                                        checked={vendor.isChecked}
                                                        onChange={() => handleWhatsappCheckboxChange(vendor.vendorId)}
                                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                        disabled={!vendor.mobileNumber}
                                                    />
                                                    <div className="vendor-info">
                                                        <span className="vendor-name">{vendor.vendorName}</span>
                                                        <span className="vendor-email">
                                                            {vendor.mobileNumber ? vendor.mobileNumber : 'No Number'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="form-actions">
                                    <button
                                        className="save-btn"
                                        onClick={handleSendWhatsapp}
                                        disabled={loadingVendors || sendingWhatsapp || selectedVendorsForWhatsapp.filter(v => v.isChecked).length === 0}
                                    >
                                        {sendingWhatsapp ? 'Sending...' : (
                                            <>
                                                <FaWhatsapp size={18} /> Send WhatsApp
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Add Vendor Modal */}
            <Modal
                isOpen={showAddVendorModal}
                onClose={() => setShowAddVendorModal(false)}
                title="Add Vendor to Request"
                width="500px"
                footer={
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', width: '100%' }}>
                        <button
                            className="swal-cancel-btn"
                            onClick={() => setShowAddVendorModal(false)}
                            style={{ margin: 0 }}
                        >
                            Cancel
                        </button>
                        <button
                            className="swal-confirm-btn"
                            onClick={handleAddVendorSubmit}
                            disabled={loadingRemainingVendors || addingVendor || !selectedVendorForAdd}
                            style={{ margin: 0, background: '#10b981' }}
                        >
                            {addingVendor ? 'Adding...' : 'Add Vendor'}
                        </button>
                    </div>
                }
            >
                <div style={{ padding: '10px 0' }}>
                    <p style={{ marginBottom: '15px', color: '#6b7280', fontSize: '0.9rem' }}>
                        Select a vendor to add to Request <strong>#{selectedPoForAddVendor?.reqNumber?.toUpperCase()}</strong>.
                    </p>
                    <div className="form-group">
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Select Vendor</label>
                        <Select
                            options={remainingVendors}
                            value={selectedVendorForAdd}
                            onChange={setSelectedVendorForAdd}
                            isLoading={loadingRemainingVendors}
                            placeholder="Search and select vendor..."
                            isClearable
                            menuPortalTarget={document.body}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: '8px',
                                    padding: '2px',
                                    borderColor: '#d1d5db',
                                    '&:hover': {
                                        borderColor: '#4f46e5'
                                    }
                                }),
                                menuPortal: (base) => ({ ...base, zIndex: 99999 })
                            }}
                        />
                    </div>
                </div>
            </Modal>

            {/* WhatsApp Notification Loader Modal */}
            {whatsappLoading && (
                <div className="whatsapp-loader-overlay">
                    <div className="whatsapp-loader-card">
                        <div className="whatsapp-icon-wrapper">
                            <FaWhatsapp className="whatsapp-anim-icon" size={50} />
                            <div className="pulse-ring"></div>
                        </div>
                        <h3>Sending Status</h3>
                        <p>Notifying vendors via WhatsApp...</p>
                        <div className="wa-progress-container">
                            <div className="wa-progress-bar">
                                <div
                                    className="wa-progress-fill"
                                    style={{ width: `${(whatsappProgress.current / whatsappProgress.total) * 100}%` }}
                                ></div>
                            </div>
                            <div className="wa-progress-stats">
                                <span>{whatsappProgress.vendorName || 'Initializing...'}</span>
                                <span>{whatsappProgress.current} / {whatsappProgress.total}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GenerateRequests;
